import React, { FC, memo, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Form } from 'antd';
import { Trans } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { getGlobalSettings } from 'app/appState';
import { BookingDocumentsNames } from 'app/appState/model/types';
import { Button } from 'shared/ui/Button';
import { getLoggedUserData, useAuthorizedState, useCheckUserDataComplete, UserAccountType } from 'entities/User';
import { WarehouseForBooking } from 'entities/Warehouse';
import { Box } from 'entities/Box';
import { ContractInfo, contractOptionsActions, useContractOptions, UnitType } from 'entities/Contract';
import { logInModalActions } from 'widgets/LogInModal';
import { CLIENT_DATE_FORMAT } from 'shared/utils/constants';
import { bookingPageActions, getUserDetailsInfo } from 'pages/BookingPage';
import { useScrollToElement } from 'shared/utils/hooks/useScrollToElement';
import { ConfirmModal } from 'shared/ui/ConfirmModal';
import { showNotification } from 'app/providers/NotificationsProvider';
import { PHONE_NUMBER_REGEX } from 'shared/utils/regex';
import { datePickerValueUnder18, disableFutureDatesUnder18 } from 'shared/utils/helpers/disable18YearsDatePicker';
import { Checkbox } from 'shared/ui/Checkbox';
import { YandexAuthButton } from 'features/YandexAuthButton';
import { VkAuthButton } from 'features/VkAuthButton';
import { setInitialUserDetailsFields } from 'widgets/UserDetailsForm';
import { ContinueButton } from 'widgets/UserDetailsForm/ui/ContinueButton';
import { ReserveBoxButton } from 'widgets/UserDetailsForm/ui/ReserveBoxButton';
import { AccountTypeSelect } from 'widgets/UserDetailsForm/ui/AccountTypeSelect';
import { useProceedToPayment } from 'widgets/UserDetailsForm/utils/hooks/useProceedToPayment';
import { useReserveBox } from 'widgets/UserDetailsForm/utils/hooks/useReserveBox';
import { GratitudeModalType, useOpenGratitudeModal } from 'features/GratitudeModal';
import { AppRoutes } from 'app/config/routerConfig/types';
import { ModernPhoneInput } from 'shared/ui/ModernPhoneInput';
import { ENTERED_USER_DETAILS_KEY } from 'widgets/UserDetailsForm/utils/constants';
import { getEnteredUserDetails } from 'widgets/UserDetailsForm/utils/helpers/getEnteredUserDetails';
import { STARTED_BOOKING_DATA_KEY } from 'pages/BookingPage/utils/constants';
import { UserDetailsInfo } from 'widgets/UserDetailsForm/model/types';
import { MobileBookingInfoStack } from 'widgets/MobileBookingInfoStack';
// Modern inputs
import { ModernInput } from 'shared/ui/ModernInput';
import { ModernDatePicker } from 'shared/ui/ModernDatePicker';
import { ModernInputPassword } from 'shared/ui/ModernInputPassword';

interface ModernUserDetailsFormMobileProps {
  warehouseInfo: Nullable<WarehouseForBooking>;
  boxInfo: Nullable<Box>;
  contractInfo: ContractInfo;
  onCompleteStep: () => void;
  onRentOptionsChange: () => void;
}

export const ModernUserDetailsFormMobile: FC<ModernUserDetailsFormMobileProps> = memo((props) => {
  const { warehouseInfo, boxInfo, contractInfo, onCompleteStep, onRentOptionsChange } = props;

  const { t } = useAppTranslation(['booking', 'common']);
  const globalSettings = useAppSelector(getGlobalSettings);
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const privacyPolicyLink = useMemo(
    () => globalSettings?.bookingDocuments.find(({ docName }) => docName === BookingDocumentsNames.PRIVACY_POLICY)?.link || '#',
    [globalSettings],
  );

  const personalDataAgreementLink = useMemo(
    () => globalSettings?.bookingDocuments.find(({ docName }) => docName === BookingDocumentsNames.PERSONAL_DATA_AGREEMENT)?.link || '#',
    [globalSettings],
  );

  const { scrollToElement, scrollRef } = useScrollToElement('center', 500);

  useLayoutEffect(() => {
    scrollToElement();
  }, [scrollToElement]);

  const [termsAgreement, setTermsAgreement] = useState(false);
  const [submitMode, setSubmitMode] = useState<Nullable<'reservation' | 'payment'>>(null);
  const [isOpenedConfirm, setOpenedConfirm] = useState(false);

  const loggedUserData = useAppSelector(getLoggedUserData);
  const userDetailsInfo = useAppSelector(getUserDetailsInfo);
  const isUserDataCompleted = useCheckUserDataComplete();
  const isAuthorized = useAuthorizedState();

  const { openGratitudeModal } = useOpenGratitudeModal();

  const userAccountType = contractInfo.accountType;

  useContractOptions({
    userAccountType,
    user: loggedUserData,
    selectedWarehouse: warehouseInfo,
    box: boxInfo,
    onRentOptionsChange,
  });

  const [form] = Form.useForm();

  useEffect(() => {
    setInitialUserDetailsFields(form, loggedUserData, dispatch, contractInfo);

    if (!loggedUserData) {
      const userDetails = getEnteredUserDetails();

      if (userDetails) {
        setInitialUserDetailsFields(form, userDetails, dispatch, contractInfo);
      }
    }
  }, [contractInfo, dispatch, form, loggedUserData]);

  const toggleTermsAgreement = useCallback((): void => {
    setTermsAgreement((prevState) => !prevState);
  }, []);

  const openLogInModal = useCallback((): void => {
    dispatch(logInModalActions.setOpened(true));
  }, [dispatch]);

  const onSuccessThirdPartyAuth = useCallback((): void => {
    setInitialUserDetailsFields(form, loggedUserData, dispatch);
  }, [dispatch, form, loggedUserData]);

  const onFailThirdPartyAuth = useCallback(
    (error: string): void => {
      console.log(error);
      showNotification('warning', t(''), t('Third party authorisation was not done'));
    },
    [t],
  );

  const handleFormChange = (): void => {
    const formValues: Partial<UserDetailsInfo> = form.getFieldsValue();

    localStorage.setItem(ENTERED_USER_DETAILS_KEY, JSON.stringify({ ...formValues, password: null }));
    dispatch(bookingPageActions.setUserDetails(formValues));
  };

  const onChangeAccountType = useCallback(
    (accountType: UserAccountType): void => {
      dispatch(contractOptionsActions.setContractOptions({ accountType }));
    },
    [dispatch],
  );

  const setReservationMode = useCallback((): void => {
    setSubmitMode('reservation');
  }, []);

  const setProceedToPaymentMode = useCallback((): void => {
    setSubmitMode('payment');
  }, []);

  const openConfirmModal = useCallback((): void => {
    setOpenedConfirm(true);
  }, []);

  const closeConfirmModal = useCallback((): void => {
    setSubmitMode(null);
    setOpenedConfirm(false);
  }, []);

  const handleOpenGratitudeModal = useCallback((): void => {
    openGratitudeModal({
      description: t(
        'Our company values each and every customer. If you have any questions or feedback, please don`t hesitate to reach out!',
      ),
      type: GratitudeModalType.RESERVATION,
      buttonId: 'reservation id',
    });
  }, [openGratitudeModal, t]);

  const onReservationSuccess = useCallback(() => {
    localStorage.removeItem(STARTED_BOOKING_DATA_KEY);

    navigate(AppRoutes.MY_BOXES);

    handleOpenGratitudeModal();
  }, [handleOpenGratitudeModal, navigate]);

  const { isReservationLoading, executeReservation } = useReserveBox({
    boxInfo,
    contractInfo,
    warehouseInfo,
    closeConfirmModal,
    formData: userDetailsInfo,
    onSuccess: onReservationSuccess,
  });

  const { isProceedToPaymentLoading, executeProceedToPayment } = useProceedToPayment({
    formData: userDetailsInfo,
    warehouseInfo,
    boxInfo,
    contractInfo,
    closeConfirmModal,
    onCompleteStep,
  });

  const isBusinessAccountType = contractInfo.accountType === UserAccountType.BUSINESS;

  return (
    <div className="h-[calc(100vh-135px)] flex flex-col">
      <ConfirmModal
        isOpened={isOpenedConfirm}
        title={t(submitMode === 'reservation' ? 'Reserve box' : 'Your details')}
        description={t(
          submitMode === 'reservation' ? 'Are you sure you want to reserve this box?' : 'Are you sure you want to save your details?',
        )}
        isLoading={submitMode === 'reservation' ? isReservationLoading : isProceedToPaymentLoading}
        onOk={submitMode === 'reservation' ? executeReservation : executeProceedToPayment}
        onCancel={closeConfirmModal}
      />

      {/* First Part: Scrollable Content with backgroundColor background */}
      <div className="flex-1 overflow-y-auto bg-backgroundColor">
        <div className="space-y-1 min-h-full flex flex-col">
          {/* Mobile Booking Info Stack */}
          {warehouseInfo && (
            <div className="bg-white rounded-lg overflow-hidden">
              <MobileBookingInfoStack warehouseInfo={warehouseInfo} boxInfo={boxInfo} contractOptions={contractInfo} />
            </div>
          )}

          {/* User Details Form Card */}
          <div className="bg-white rounded-lg p-4 flex-1 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold text-2xl" ref={scrollRef}>
                {t(!isAuthorized ? 'Registration' : 'Fill in empty fields to continue')}
              </div>
              {!isAuthorized && (
                <div className="flex items-center text-sm text-primaryLight font-normal">
                  <Button className="ml-1" theme="clear" fontSize="small" onClick={openLogInModal}>
                    <span className="underline">{t('Already have an account?')}</span>
                  </Button>
                </div>
              )}
            </div>

            <Form
              className="flex flex-col flex-1"
              name="modernUserDetails"
              form={form}
              autoComplete="off"
              onFinish={openConfirmModal}
              onValuesChange={handleFormChange}
            >
              <div className="flex flex-col flex-1">
                <div className="space-y-6">
                  {!isAuthorized && (
                    <div className="flex gap-4 items-center justify-start">
                      <YandexAuthButton onSuccessAuth={onSuccessThirdPartyAuth} onFailAuth={onFailThirdPartyAuth} />
                      <VkAuthButton
                        className="flex gap-4 items-center"
                        onSuccessAuth={onSuccessThirdPartyAuth}
                        onFailAuth={onFailThirdPartyAuth}
                      />
                    </div>
                  )}

                  <div>
                    {isAuthorized && !isUserDataCompleted && <AccountTypeSelect onChange={onChangeAccountType} />}

                    {/* User info - Mobile Layout */}
                    <div className="space-y-4 mb-6">
                      <div className="text-lg font-semibold">{t('User info')}</div>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <div className="text-sm mb-1 text-primaryLight">{t('Last name')}</div>
                            <Form.Item
                              name="lastName"
                              rules={[
                                { required: true, message: t('Please enter your last name!') },
                                { min: 1, max: 30, message: t('Last name must have 1 - 30 characters!') },
                              ]}
                            >
                              <ModernInput />
                            </Form.Item>
                          </div>
                          <div>
                            <div className="text-sm mb-1 text-primaryLight">{t('First name')}</div>
                            <Form.Item
                              name="firstName"
                              rules={[
                                { required: true, message: t('Please enter your first name!') },
                                { min: 1, max: 30, message: t('First name must have 1 - 30 characters!') },
                              ]}
                            >
                              <ModernInput />
                            </Form.Item>
                          </div>
                        </div>

                        {!isAuthorized && (
                          <div>
                            <div className="text-sm mb-1 text-primaryLight">{t('Email')}</div>
                            <Form.Item
                              name="email"
                              rules={[
                                { required: true, message: t('Please enter your email!') },
                                { type: 'email', message: t('Please enter correct email!') },
                              ]}
                            >
                              <ModernInput />
                            </Form.Item>
                          </div>
                        )}

                        <div>
                          <div className="text-sm mb-1 text-primaryLight">{t('Phone')}</div>
                          <Form.Item
                            name="phone"
                            rules={[
                              { required: true, message: t('Please enter your phone!') },
                              { pattern: PHONE_NUMBER_REGEX, message: t('Please provide a valid phone number. Example: +123456789012345') },
                            ]}
                          >
                            <ModernPhoneInput />
                          </Form.Item>
                        </div>

                        {globalSettings?.isCustomerIdentificationNumberRequired && (
                          <div>
                            <div className="text-sm mb-1 text-primaryLight">
                              {t(isBusinessAccountType ? 'Business identification number' : 'Personal identification number')}
                            </div>
                            <Form.Item
                              name="identificationNumber"
                              rules={[{ required: true, message: t('Please enter your identification number!') }]}
                            >
                              <ModernInput />
                            </Form.Item>
                          </div>
                        )}

                        <div>
                          <div className="text-sm mb-1 text-primaryLight">{t('Date of birth')}</div>
                          <Form.Item name="dateOfBirth" rules={[{ required: true, message: t('Please enter your date of birth!') }]}>
                            <ModernDatePicker
                              className="w-full"
                              placeholder={' '}
                              format={CLIENT_DATE_FORMAT}
                              inputReadOnly
                              disabledDate={disableFutureDatesUnder18}
                              defaultPickerValue={datePickerValueUnder18}
                            />
                          </Form.Item>
                        </div>

                        {isBusinessAccountType && (
                          <div>
                            <div className="text-sm mb-1 text-primaryLight">{t('Company')}</div>
                            <Form.Item
                              name="company"
                              rules={[
                                { required: true, message: t('Please enter your company name!') },
                                { min: 1, max: 100, message: t('Company name must have 1 - 100 characters!') },
                              ]}
                            >
                              <ModernInput />
                            </Form.Item>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Address - Mobile Layout */}
                    <div className="space-y-4 mb-6">
                      <div className="text-lg font-semibold">{t('Address')}</div>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <div className="text-sm mb-1 text-primaryLight">{t('Street')}</div>
                            <Form.Item
                              name="street"
                              rules={[
                                { required: true, message: t('Please enter your street!') },
                                { min: 2, message: t('Street must have at least 2 characters!') },
                              ]}
                            >
                              <ModernInput />
                            </Form.Item>
                          </div>
                          <div>
                            <div className="text-sm mb-1 text-primaryLight">{t('House')}</div>
                            <Form.Item
                              name="house"
                              rules={[
                                { required: true, message: t('Please enter your house!') },
                                { min: 1, max: 30, message: t('House must have 1 - 30 characters!') },
                              ]}
                            >
                              <ModernInput />
                            </Form.Item>
                          </div>
                        </div>

                        <div>
                          <div className="text-sm mb-1 text-primaryLight">{t('Apartment')}</div>
                          <Form.Item name="apartment" rules={[{ max: 30, message: t('Apartment must have no more than 30 characters!') }]}>
                            <ModernInput />
                          </Form.Item>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <div className="text-sm mb-1 text-primaryLight">{t('City')}</div>
                            <Form.Item
                              name="city"
                              rules={[
                                { required: true, message: t('Please enter your city!') },
                                { min: 2, max: 30, message: t('City must have 2 - 30 characters!') },
                              ]}
                            >
                              <ModernInput />
                            </Form.Item>
                          </div>
                          <div>
                            <div className="text-sm mb-1 text-primaryLight">{t('Country')}</div>
                            <Form.Item
                              name="country"
                              rules={[
                                { required: true, message: t('Please enter your country!') },
                                { min: 2, max: 30, message: t('Country must have 2 - 30 characters!') },
                              ]}
                            >
                              <ModernInput />
                            </Form.Item>
                          </div>
                        </div>

                        <div>
                          <div className="text-sm mb-1 text-primaryLight">{t('Zip code')}</div>
                          <Form.Item
                            name="zip"
                            // rules={[
                            //   { required: true, message: t('Please enter your zip code!') },
                            //   { min: 2, max: 30, message: t('Zip code must have 2 - 30 characters!') },
                            // ]}
                          >
                            <ModernInput />
                          </Form.Item>
                        </div>
                      </div>
                    </div>

                    {/* Password - Mobile Layout */}
                    {!isAuthorized && (
                      <div className="space-y-4 mb-6">
                        <div className="text-lg font-semibold">{t('Password')}</div>
                        <div className="space-y-4">
                          <div>
                            <div className="text-sm mb-1 text-primaryLight">{t('Password')}</div>
                            <Form.Item
                              name="password"
                              rules={[
                                { required: true, message: t('Please enter your password!') },
                                { min: 8, max: 20, message: t('Password must have 8 - 20 characters!') },
                              ]}
                            >
                              <ModernInputPassword />
                            </Form.Item>
                          </div>
                          <div>
                            <div className="text-sm mb-1 text-primaryLight">{t('Repeat password')}</div>
                            <Form.Item
                              name="repeatPassword"
                              dependencies={['password']}
                              rules={[
                                { required: true, message: t('Please repeat your password!') },
                                ({ getFieldValue }) => ({
                                  async validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                      return;
                                    }
                                    throw new Error(t('Passwords do not match'));
                                  },
                                }),
                              ]}
                            >
                              <ModernInputPassword />
                            </Form.Item>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Terms Agreement Section - Fixed at bottom */}
                <div className="mt-auto pt-4">
                  <Checkbox
                    className="[&>*]:text-sm [&>*]:font-normal [&>*]:text-primaryLight"
                    name="agreement"
                    checked={termsAgreement}
                    onChange={toggleTermsAgreement}
                  >
                    {boxInfo?.unitType === UnitType.OFFICE ? (
                      <Trans
                        t={t}
                        i18nKey="I have read and agree with the <0>Privacy Policy</0>"
                        components={[<Link key="0" className="text-accent text-sm" to={privacyPolicyLink} target="_blank" />]}
                      />
                    ) : (
                      <Trans
                        t={t}
                        i18nKey="I have read and agree with the <0>Privacy Policy</0> and <1>Personal data Agreement</1>"
                        components={[
                          <Link key="0" className="text-accent text-sm" to={privacyPolicyLink} target="_blank" />,
                          <Link key="1" className="text-accent text-sm" to={personalDataAgreementLink} target="_blank" />,
                        ]}
                      />
                    )}
                  </Checkbox>
                </div>
              </div>
            </Form>
          </div>
        </div>
      </div>

      {/* Second Part: Fixed Action Buttons with backgroundColor background */}
      <div className="bg-backgroundColor py-4 flex-shrink-0">
        <div className="flex space-x-2">
          <ReserveBoxButton className="w-full" isDisabled={!termsAgreement} formInstance={form} setReservationMode={setReservationMode} />
          <ContinueButton
            label={boxInfo?.unitType === UnitType.OFFICE ? t('Continue') : t('Proceed to payment')}
            isDisabled={!termsAgreement}
            formInstance={form}
            setProceedToPaymentMode={setProceedToPaymentMode}
          />
        </div>
      </div>
    </div>
  );
});
